import React, { useRef } from 'react';
import { AvTooltip, FieldListObject } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Button, Collapse, List, Typography, useTheme } from '@mui/material';
import { useAvContext } from '../../context/AvContextProvider';
import { sortByStringField } from '../../utils/utils';
import { fieldTypeIconsMap } from '../ModelManagement/hooks';
import { MappingItem } from '../Sources/Mapping/MappingItem';
import { DragItem } from '../Sources/Mapping/types';

export interface DragItemEntity extends FieldListObject {
  type: number;
}
interface EntityProps {
  displayName: string;
  expanded?: boolean;
  setExpand: (displayName: string) => void;
  collapseProps: any;
  onItemDoubleClick?: (dragItem: DragItemEntity) => void;
  onItemClick?: (dragItem: DragItemEntity) => void;
  onDragStart: (dragItem: DragItemEntity) => void;
  items: FieldListObject[] | { title: string; value: string }[];
  nameMap?: { [key: string]: string };
  usedFields?: string[];
  type?: string;
}
const EntityItem: React.FC<EntityProps> = ({
  displayName,
  expanded = false,
  setExpand,
  collapseProps,
  onItemDoubleClick,
  onItemClick,
  onDragStart,
  items: originalItems,
  usedFields,
  type,
}) => {
  const theme = useTheme();
  const { fieldTypeMap } = useAvContext().accountEntities;
  const ref = useRef<any>(undefined);
  const getDragItem = field => ({ type: DragItem.Entity, ...field });

  const items = sortByStringField(originalItems, 'title');
  const getType = (pathAlias, CustomType) => {
    const type = CustomType || fieldTypeMap[pathAlias];
    return fieldTypeIconsMap()[Array.isArray(type) ? JSON.stringify(type) : type] || type;
  };

  const entityItemText = `${displayName} (${items.length})`;
  return (
    <Box key={displayName}>
      <Box sx={{ ...flex.justifyBetweenCenter }}>
        <Button ref={ref} onClick={() => setExpand(displayName)} disabled={!items.length}>
          <ArrowDown
            style={{
              opacity: !items.length ? 0.4 : 1,
              color: 'var(--color-content-base-secondary)',
              transform: `rotate(${expanded ? 0 : -90}deg)`,
              transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
            }}
          />
          <Typography variant="h7" sx={{ ...flex.row, width: '240px', opacity: !items.length ? 0.4 : 1 }}>
            <AvTooltip>{entityItemText}</AvTooltip>
          </Typography>
        </Button>
      </Box>
      <Box sx={{ ...flex.col, gap: 2 }}>
        <Collapse in={!!(expanded && items?.length)} {...collapseProps}>
          <List sx={{ gap: 1, ...flex.col }}>
            {items.map(({ value, title, pathAlias, ...props }) => (
              <MappingItem
                key={value}
                size="small"
                isUsed={usedFields?.includes(value)}
                label={
                  <>
                    {getType(value, type)}
                    <Box sx={{ flex: 1, ...ellipsis }}>
                      <AvTooltip title={title || value} muiProps={{ placement: 'top' }}>
                        {title || value}
                      </AvTooltip>
                    </Box>
                  </>
                }
                onDoubleClick={() =>
                  onItemDoubleClick ? onItemDoubleClick(getDragItem({ value, title, pathAlias, ...props })) : undefined
                }
                onClick={onItemClick && (() => onItemClick(getDragItem({ value, title, pathAlias, ...props })))}
                onDragStart={() => onDragStart(getDragItem({ value, title, pathAlias, ...props }))}
              />
            ))}
          </List>
        </Collapse>
      </Box>
    </Box>
  );
};

export default EntityItem;
